import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesChatMessages } from "@/i18n/messages/frames-chat";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { useFrameSurface } from "../shared/use-frame-surface";

interface ChatFrameMessage {
  id: string;
  from?: string;
  mine: boolean;
  text: string;
  time: string;
  read?: boolean;
}

function toMessage(raw: unknown, index: number): ChatFrameMessage | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.text !== "string") return null;
  return {
    id: typeof m.id === "string" ? m.id : `msg-${index}`,
    from: typeof m.from === "string" ? m.from : undefined,
    mine: m.mine === true,
    text: m.text,
    time: typeof m.time === "string" ? m.time : "",
    read: typeof m.read === "boolean" ? m.read : undefined,
  };
}

/**
 * Production ChatFrame — read-only WhatsApp-style message thread.
 *
 * Bubble recipe copied from the workshop chat-frame (mine bg-accent,
 * theirs bg-elevated, rounded-preview, no border). No
 * FrameHeader/FrameFooter: canvas drag uses the hover chrome drag zone,
 * and follow-ups/comments/resize/delete come free via the wrap() HOC chain
 * + hover chrome. Content renders from data.content ({title, messages});
 * agent frame.update broadcasts re-render automatically.
 */
export function ChatFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesChatMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const rawMessages = Array.isArray(content.messages)
    ? content.messages
    : undefined;
  const messages =
    rawMessages !== undefined
      ? rawMessages
          .map((raw, index) => toMessage(raw, index))
          .filter((m): m is ChatFrameMessage => m !== null)
      : [];
  const status = data.status;
  const isEmpty = messages.length === 0;

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="chat-frame"
      {...frameSurface.attributes}
    >
      <div
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-4 [--denk-frame-content-padding-block-start:16px] [--denk-frame-content-padding-block-end:16px] [--denk-frame-content-padding-inline:16px]"
        data-frame-content="true"
        data-frame-drag-handle="true"
        onClick={(e) => e.stopPropagation()}
      >
        {isEmpty ? (
          status === "streaming" ? (
            <div
              className="flex justify-start"
              data-testid="chat-frame-typing"
              aria-label={t("loadingMessages")}
            >
              <div className="flex items-center gap-1 rounded-preview p-4">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted"
                    aria-hidden="true"
                  />
                ))}
                <span className="sr-only">{t("loadingMessages")}</span>
              </div>
            </div>
          ) : (
            <FrameEmptyState
              frameId={id}
              status={status}
              testId="chat-frame-empty"
            >
              {t("noMessages")}
            </FrameEmptyState>
          )
        ) : (
          messages.map((msg, idx) => {
            const isLast = idx === messages.length - 1;
            return (
              <div
                key={msg.id}
                className={cn(
                  "flex w-full",
                  msg.mine ? "justify-end" : "justify-start",
                )}
                data-testid={`chat-frame-msg-${msg.id}`}
              >
                {msg.mine ? (
                  <div className="max-w-[84%] whitespace-pre-wrap rounded-preview px-3 py-2 text-appkit-body leading-relaxed text-primary">
                    <p className="break-words">
                      {msg.text}
                      {isLast && status === "streaming" && (
                        <span
                          className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                          aria-label={t("streaming")}
                        />
                      )}
                    </p>
                    <div className="mt-0.5 flex items-center justify-end gap-1">
                      <span className="text-appkit-small text-muted">
                        {msg.time}
                      </span>
                      {msg.read !== undefined && (
                        <Icons.CheckCheck
                          className={cn(
                            "h-3 w-3",
                            msg.read ? "text-accent" : "text-subtle",
                          )}
                          aria-label={msg.read ? t("read") : t("sent")}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[84%] whitespace-pre-wrap rounded-preview px-3 py-2 text-appkit-body leading-relaxed text-primary">
                    {msg.from && (
                      <p className="text-appkit-small font-semibold text-accent">
                        {msg.from}
                      </p>
                    )}
                    <p className="break-words">
                      {msg.text}
                      {isLast && status === "streaming" && (
                        <span
                          className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                          aria-label={t("streaming")}
                        />
                      )}
                    </p>
                    <div className="mt-0.5 flex items-center justify-end gap-1">
                      <span className="text-appkit-small text-muted">
                        {msg.time}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
